// LOI-to-close timeline (202611021430, backend app/close_timeline.py).
// The seller deal's LOI signed date sets the baseline close: LOI + the median
// LOI → close span of closed deals. Until enough deals have closed, the
// baseline is the est. close a person set. Each milestone's target counts back
// from it. Checking a milestone records the actual date; a late one projects
// the slip. Before LOI there is no card: the close date stays blank.
//
// Exports:
//   window.CloseTimelineCard   the deal page card (checklist + baseline / projected)
//   window.CloseDateHint       the slip / suggestion beside Key facts' Est. close
//   window.useCloseTimeline    the timeline both read: one request per deal
//   window.RetainerCell        Key facts' Retainer select + amount (every deal page)

const CT_DIM = { color: 'var(--text-4)' };
const ctToday = () => window.localISODate(new Date());
const ctDays = (n) => `${n}d`;
const ctBtn = {
  background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
  color: 'var(--accent-text)', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2, whiteSpace: 'nowrap',
};

// The card and the Est. close hint mount together and read the same
// timeline: both join one in-flight request (keyed by the deal fields that
// change it), and a write from either is broadcast so the other never shows
// a stale slip.
const CT_EVENT = 'close-timeline-updated';
const CT_INFLIGHT = new Map();
const ctFetch = (id, key) => {
  const k = `${id}|${key}`;
  if (!CT_INFLIGHT.has(k)) {
    CT_INFLIGHT.set(k, window.AtlasApi.getCloseTimeline(id).finally(() => setTimeout(() => CT_INFLIGHT.delete(k), 0)));
  }
  return CT_INFLIGHT.get(k);
};
const useCloseTimeline = (deal) => {
  const raw = (deal && deal._raw) || {};
  const id = deal && deal.id;
  const seller = !!deal && deal.type !== 'buyer_buyside' && !window.AtlasIsGuest;
  // undefined = loading, null = n/a, { error } = the read failed
  const [tl, setTl] = React.useState(undefined);
  const [attempt, setAttempt] = React.useState(0);
  const key = [raw.loi_signed_date, raw.loi_date, raw.estimated_close_date, raw.stage_id, raw.exit_id, attempt].join('|');
  React.useEffect(() => {
    if (!seller || !id) { setTl(null); return undefined; }
    let alive = true;
    ctFetch(id, key).then((d) => { if (alive) setTl(d); })
      .catch((e) => { if (alive) setTl({ error: (e && e.message) || 'Could not load' }); });
    return () => { alive = false; };
  }, [id, seller, key]);
  React.useEffect(() => {
    const on = (e) => { const d = e.detail || {}; if (d.id === id) setTl(d.timeline); };
    window.addEventListener(CT_EVENT, on);
    return () => window.removeEventListener(CT_EVENT, on);
  }, [id]);
  const publish = React.useCallback((timeline) => {
    window.dispatchEvent(new CustomEvent(CT_EVENT, { detail: { id, timeline } }));
  }, [id]);
  const retry = React.useCallback(() => { setTl(undefined); setAttempt((n) => n + 1); }, []);
  return [tl, publish, retry];
};

// "+5d" amber when late, "−2d" muted when early, "on time" otherwise.
const DeltaTag = ({ days }) => {
  if (days == null) return null;
  if (days > 0) return <span className="num" style={{ color: 'var(--amber-fg)', fontSize: 11, fontWeight: 600 }} title={`${days} days after target`}>+{ctDays(days)}</span>;
  if (days < 0) return <span className="num" style={{ ...CT_DIM, fontSize: 11 }} title={`${-days} days before target`}>−{ctDays(-days)}</span>;
  return <span style={{ ...CT_DIM, fontSize: 11 }}>on time</span>;
};

// One checklist row: checkbox · label · target · status (actual date or lateness).
const MilestoneRow = ({ m, canWrite, closed, onSet }) => {
  const id = `ct-${m.key}`;
  const [busy, setBusy] = React.useState(false);
  const done = !!m.completed_on;
  const toggle = async () => {
    setBusy(true);
    try { await onSet(m.key, done ? null : ctToday()); } finally { setBusy(false); }
  };
  let status = null;
  if (done) {
    status = (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <window.EditableCell type="date" label={`${m.label} done`} value={m.completed_on} editable={canWrite}
          displayAs={<span className="num" style={{ color: 'var(--text-2)' }}>{window.fmtDate(m.completed_on)}</span>}
          onSave={(v) => (v ? onSet(m.key, v) : onSet(m.key, null))} />
        {!closed && <DeltaTag days={m.delta_days} />}
      </span>
    );
  } else if (m.status === 'late') {
    status = <window.Pill hue="rose" sz="sm" title={`Target ${window.fmtDate(m.target)}`}>{ctDays(m.delta_days)} late</window.Pill>;
  } else if (m.status === 'due') {
    status = <window.Pill hue="amber" sz="sm">due {window.fmtDate(m.target)}</window.Pill>;
  }
  return (
    <div data-milestone={m.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 14px', minHeight: 30, minWidth: 0 }}>
      <input id={id} type="checkbox" checked={done} disabled={!canWrite || busy} onChange={toggle}
        style={{ margin: 0, flexShrink: 0, accentColor: 'var(--accent)', cursor: canWrite ? 'pointer' : 'default' }} />
      <label htmlFor={id} style={{ flex: '1 1 auto', minWidth: 0, fontSize: 12, color: done ? 'var(--text-3)' : 'var(--text)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: canWrite ? 'pointer' : 'default' }}
        title={`${m.label} · ${m.days_before_close} days before close`}>{m.label}</label>
      {m.target && m.status === 'pending' && (
        <span className="num" style={{ ...CT_DIM, fontSize: 11, whiteSpace: 'nowrap' }} title="Target">{window.fmtDate(m.target)}</span>
      )}
      {status && <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>{status}</span>}
    </div>
  );
};

// A tile in the card's top strip.
const CtTile = ({ label, value, sub, subHue, tip }) => (
  <div style={{ minWidth: 0, flex: '1 1 0' }} title={tip}>
    <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</div>
    <div className="num" style={{ fontSize: 14, fontWeight: 600, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value || <span style={CT_DIM}>—</span>}</div>
    {sub && <div style={{ fontSize: 10.5, color: subHue || 'var(--text-4)', whiteSpace: 'nowrap' }}>{sub}</div>}
  </div>
);

// What the baseline rests on, for the Baseline tile's sub line + tooltip.
const ctBasis = (tl) => {
  const n = tl.sample || 0;
  const deals = `${n} closed deal${n === 1 ? '' : 's'}`;
  if (tl.basis === 'history') return { sub: `LOI + ${ctDays(tl.span_days)}`, tip: `Median LOI to close of ${deals}` };
  const few = `Only ${deals} with an LOI and close date (needs ${tl.min_sample})`;
  if (tl.basis === 'close_date') return { sub: 'est. close', tip: `${few}: counting back from the est. close` };
  return { sub: 'no est. close', tip: `${few}: set an est. close to get milestone targets` };
};

const ctPreview = (tl) => {
  if (!tl || !tl.projected_close) return '—';
  const slip = tl.slip_days > 0 ? ` · +${ctDays(tl.slip_days)} slip` : tl.closed ? '' : ' · on track';
  const done = tl.milestones.filter((m) => m.completed_on).length;
  return `Close ${window.fmtDate(tl.projected_close)}${slip} · ${done}/${tl.milestones.length}`;
};

const CloseTimelineCard = ({ deal, canWrite, onSave }) => {
  const [tl, publish, retry] = useCloseTimeline(deal);
  const raw = (deal && deal._raw) || {};
  if (tl && tl.error) {
    // Only a deal with an LOI would have shown the card.
    if (!raw.loi_signed_date && !raw.loi_date) return null;
    return (
      <window.Section title="Closing timeline" collapsible id="deal.close_timeline">
        <div data-close-timeline-error="" role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text-3)' }}>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tl.error}>Couldn't load the timeline</span>
          <button type="button" style={ctBtn} onClick={retry}>Retry</button>
        </div>
      </window.Section>
    );
  }
  if (!tl || !tl.loi_date) return null;               // loading, n/a, or before LOI: no card
  const set = async (key, completed_on) => {
    try {
      publish(await window.AtlasApi.setMilestone(deal.id, key, completed_on));
    } catch (e) {
      window.pushToast && window.pushToast({ type: 'error', title: 'Save failed', body: e.message });
    }
  };
  const slip = tl.slip_days || 0;
  const late = tl.milestones.filter((m) => m.status === 'late').length;
  const basis = ctBasis(tl);
  return (
    <window.Section title="Closing timeline" collapsible id="deal.close_timeline" preview={ctPreview(tl)}
      action={late ? <window.Pill hue="rose" sz="sm">{late} late</window.Pill> : null}>
      <div style={{ display: 'flex', gap: 12, padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
        <CtTile label="LOI signed" value={window.fmtDate(tl.loi_date)} sub={tl.loi_source === 'dealcloud' ? 'stage date' : null} />
        <CtTile label="Baseline" value={tl.baseline_close ? window.fmtDate(tl.baseline_close) : null} sub={basis.sub} tip={basis.tip} />
        <CtTile label="Projected" value={tl.projected_close ? window.fmtDate(tl.projected_close) : null}
          sub={tl.closed || !tl.projected_close ? null : slip > 0 ? `+${ctDays(slip)} slip` : 'on track'}
          subHue={slip > 0 ? 'var(--amber-fg)' : null} tip="From the latest checked milestone and the ones still open" />
      </div>
      {canWrite && tl.suggested_close && (
        <div data-close-suggestion="" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '7px 14px', fontSize: 12,
          borderBottom: '1px solid var(--border)', background: slip > 0 ? 'var(--amber-bg)' : 'var(--surface-2)', color: slip > 0 ? 'var(--amber-fg)' : 'var(--text-2)' }}>
          <span>Est. close {tl.estimated_close_date ? window.fmtDate(tl.estimated_close_date) : '—'}</span>
          <button type="button" style={{ ...ctBtn, color: 'inherit' }}
            onClick={() => onSave({ estimated_close_date: tl.suggested_close, close_date_source: tl.basis === 'history' ? 'timeline' : 'ui' })}>
            Use {window.fmtDate(tl.suggested_close)}
          </button>
        </div>
      )}
      <div role="group" aria-label="Closing milestones" style={{ padding: '4px 0 6px' }}>
        {tl.milestones.map((m) => <MilestoneRow key={m.key} m={m} canWrite={canWrite} closed={tl.closed} onSet={set} />)}
      </div>
    </window.Section>
  );
};

// Beside Key facts' Est. close: the projected slip (amber), or a muted
// "timeline" while the date on file is still the one the timeline wrote. The "Use <date>" offer lives
// in the card — the Key facts row is too narrow for both.
const CloseDateHint = ({ deal }) => {
  const [tl] = useCloseTimeline(deal);
  if (!tl || tl.error || !tl.loi_date || tl.closed) return null;
  const slip = tl.slip_days || 0;
  if (slip > 0) {
    return (
      <span className="num" style={{ color: 'var(--amber-fg)', fontWeight: 600 }}
        title={`Projected ${window.fmtDate(tl.projected_close)} from the milestones`}>+{ctDays(slip)}</span>
    );
  }
  if (!tl.suggested_close && tl.timeline_owned) {
    return <span title="Set from the LOI-to-close timeline">timeline</span>;
  }
  return null;
};

// Retainer (202611021430): — / Yes (+ amount) / No. "—" and "No" both clear
// the amount.
const RETAINER_OPTIONS = [
  { value: null, label: '—' },
  { value: 'yes', label: 'Yes', hue: 'teal' },
  { value: 'no', label: 'No' },
];
const RetainerCell = ({ deal, canWrite, onSave, align }) => {
  const raw = (deal && deal._raw) || {};
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', gap: 8, minWidth: 0 }}>
      <window.EditableCell type="select" label="Retainer" value={raw.retainer == null ? null : raw.retainer ? 'yes' : 'no'} options={RETAINER_OPTIONS}
        editable={canWrite} align={align}
        onSave={(v) => onSave(v === 'yes' ? { retainer: true } : { retainer: v === 'no' ? false : null, retainer_amount: null })} />
      {raw.retainer && (
        <window.EditableCell type="currency" label="Retainer amount" value={raw.retainer_amount ?? null} editable={canWrite} align={align}
          onSave={(retainer_amount) => onSave({ retainer_amount })} />
      )}
    </span>
  );
};

Object.assign(window, { CloseTimelineCard, CloseDateHint, useCloseTimeline, RetainerCell });
